import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Wallet, TrendingUp, TrendingDown,
  Flame, ChevronRight,
} from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { useGamification } from '@hooks/useGamification';
import { useDashboard } from '@hooks/useDashboard';
import { useMarketData } from '@hooks/useMarketData';
import { useCashFlow } from '@hooks/useCashFlow';
import { useBalanceEvolution, useCashFlowProjection, useCategoryReport, useSummaryReport } from '@hooks/useReports';
import { useTransactions } from '@hooks/useTransactions';
import Modal from '@components/common/Modal';
import PageTutorial from '@components/common/PageTutorial';
import TransactionForm from '@components/transactions/TransactionForm';
import { clsx } from 'clsx';

import { pct, formatCompact } from '@components/dashboard/dashboardHelpers';
import MarketQuotes from '@components/dashboard/MarketQuotes';
import DashboardHero from '@components/dashboard/DashboardHero';
import MetricCard from '@components/dashboard/MetricCard';
import SavingsRing from '@components/dashboard/SavingsRing';
import DashboardCharts from '@components/dashboard/DashboardCharts';
import DashboardCashFlow from '@components/dashboard/DashboardCashFlow';
import DashboardUpcoming from '@components/dashboard/DashboardUpcoming';
import DashboardRecentTransactions from '@components/dashboard/DashboardRecentTransactions';

const Dashboard = () => {
  const { user } = useAuth();
  const { createTransaction, isCreating } = useTransactions();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  const upcomingStart = now.toISOString().split('T')[0];
  const upcomingEnd = new Date(now.getTime() + 30 * 86400_000).toISOString().split('T')[0];

  // Data hooks
  const { data, isLoading: dashLoading } = useDashboard({ startDate, endDate });
  const { data: balanceData = [] } = useBalanceEvolution(6);
  const { data: summaryData } = useSummaryReport();
  const { data: projectionData } = useCashFlowProjection(6);
  const { data: categoryData } = useCategoryReport(startDate, endDate);
  const { quotes, isLoading: marketLoading } = useMarketData();
  const { profile: gamProfile } = useGamification();
  const { items: upcomingItems } = useCashFlow({ startDate: upcomingStart, endDate: upcomingEnd });

  const { currentBalance, monthSummary, accounts, recentTransactions } = data;

  // Derived
  const savingsRate = useMemo(() => {
    if (!monthSummary.income) return 0;
    return ((monthSummary.income - monthSummary.expense) / monthSummary.income) * 100;
  }, [monthSummary]);

  const heroChange = useMemo(() => {
    if (balanceData.length < 2) return null;
    const last = balanceData[balanceData.length - 1]?.balance ?? 0;
    const prev = balanceData[balanceData.length - 2]?.balance ?? 0;
    return { amount: last - prev, pct: pct(last, prev) };
  }, [balanceData]);

  const topExpenseCategories = useMemo(() => {
    const cats = categoryData?.expense?.categories ?? [];
    return cats.slice(0, 5);
  }, [categoryData]);

  const upcomingPayments = useMemo(() =>
    upcomingItems
      .filter(i => i.status === 'pending' && i.type === 'expense')
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 5),
    [upcomingItems]);

  const projectionChartData = useMemo(() =>
    (projectionData?.projection ?? []).map(p => ({ ...p, label: p.monthLabel })),
    [projectionData]);

  const handleCreate = async (formData) => {
    await createTransaction(formData);
    setIsModalOpen(false);
  };

  return (
    <div className="space-y-6 pb-8">
      <PageTutorial
        pageId="dashboard"
        title="Seu painel financeiro"
        steps={[
          'O ticker de mercado mostra cotacoes em tempo real de acoes, moedas e criptomoedas',
          'O patrimonio total soma o saldo de todas as suas contas',
          'Os cards de metricas mostram receitas, despesas e saldo do mes atual',
          'A projecao de fluxo de caixa usa suas recorrencias e parcelamentos para prever o futuro',
          'Clique em \'Nova Transacao\' para registrar uma movimentacao rapidamente',
        ]}
      />

      <MarketQuotes quotes={quotes} isLoading={marketLoading} />

      {/* Gamification Widget */}
      {gamProfile && (
        <Link to="/app/gamification" className="block">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-4 hover:shadow-md transition-shadow">
            <div className={clsx(
              'w-10 h-10 rounded-full bg-gradient-to-br flex items-center justify-center text-white font-bold text-sm shadow',
              gamProfile.level >= 8 ? 'from-red-400 to-red-600' :
              gamProfile.level >= 5 ? 'from-purple-400 to-purple-600' :
              gamProfile.level >= 3 ? 'from-blue-400 to-blue-600' : 'from-gray-400 to-gray-500'
            )}>
              {gamProfile.level}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{gamProfile.levelTitle}</span>
                <span className="text-xs text-gray-500">{(gamProfile.xp || 0).toLocaleString('pt-BR')} XP</span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full mt-1 overflow-hidden">
                <div className="h-full bg-primary-500 rounded-full transition-all" style={{ width: `${gamProfile.xpProgress || 0}%` }} />
              </div>
            </div>
            <div className="flex items-center gap-1 text-orange-500 flex-shrink-0">
              <Flame size={16} />
              <span className="text-sm font-bold">{gamProfile.streak || 0}</span>
            </div>
            <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
          </div>
        </Link>
      )}

      <DashboardHero
        user={user}
        currentBalance={currentBalance}
        monthSummary={monthSummary}
        accounts={accounts}
        balanceData={balanceData}
        heroChange={heroChange}
        dashLoading={dashLoading}
        onNewTransaction={() => setIsModalOpen(true)}
      />

      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Receitas" value={monthSummary.income} change={summaryData?.variation?.income}
          positive={true}
          icon={<TrendingUp size={20} className="text-emerald-600" />}
          iconBg="bg-emerald-100 dark:bg-emerald-900/30" valueColor="text-emerald-600"
          sparkData={balanceData} sparkKey="income" sparkColor="#22c55e"
        />
        <MetricCard
          title="Despesas" value={monthSummary.expense}
          change={summaryData?.variation?.expense != null ? -summaryData.variation.expense : null}
          positive={false}
          icon={<TrendingDown size={20} className="text-red-600" />}
          iconBg="bg-red-100 dark:bg-red-900/30" valueColor="text-red-600"
          sparkData={balanceData} sparkKey="expense" sparkColor="#ef4444"
        />
        <MetricCard
          title="Saldo do Mês" value={monthSummary.balance}
          icon={<Wallet size={20} className={monthSummary.balance >= 0 ? 'text-primary-600' : 'text-red-600'} />}
          iconBg={monthSummary.balance >= 0 ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-red-100 dark:bg-red-900/30'}
          valueColor={monthSummary.balance >= 0 ? 'text-primary-600' : 'text-red-600'}
          sparkData={balanceData} sparkKey="balance" sparkColor="#0ea5e9"
        />
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm flex flex-col items-center justify-center gap-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 self-start w-full">
            Taxa de Poupança
          </p>
          <SavingsRing rate={savingsRate} size={80} />
          <p className="text-xs text-gray-400 text-center leading-tight">
            {savingsRate >= 0 ? `Poupando ${savingsRate.toFixed(0)}% da renda` : 'Gastando mais que ganha'}
          </p>
        </div>
      </div>

      <DashboardCharts
        balanceData={balanceData}
        topExpenseCategories={topExpenseCategories}
        categoryTotal={categoryData?.expense?.total}
      />

      <DashboardCashFlow
        projectionChartData={projectionChartData}
        currentBalance={projectionData?.currentBalance}
      />

      <div className="grid lg:grid-cols-2 gap-4">
        <DashboardUpcoming upcomingPayments={upcomingPayments} />
        <DashboardRecentTransactions recentTransactions={recentTransactions} />
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Nova Transação" size="md">
        <TransactionForm onSubmit={handleCreate} onCancel={() => setIsModalOpen(false)} isLoading={isCreating} />
      </Modal>
    </div>
  );
};

export default Dashboard;
